/**
 * groupHistoryHeader.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
 */

import * as React from "react";

import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SettingsIcon from "@mui/icons-material/Settings";

import { CircularProgress, ClickAwayListener, Divider, IconButton, ListItemIcon, 
  ListItemText, MenuItem, MenuList, Paper, Popper, Tooltip } from "@mui/material";

import { GroupMembership, GroupMemberType, GroupSubmissionType, GroupType } from "../models/group";
import { GroupNavContext } from "../chat/groupNav.context";

interface GroupsHistoryHeaderProps {
  loadingGroup: boolean,
  group?: GroupMembership | null,
  className: string,
  //doAddMember(): void,
  doRemoveThisDevice(): Promise<void>
}

export function GroupsHistoryHeader(props: GroupsHistoryHeaderProps): React.ReactElement<any, any> {
  const [openMenu, setOpenMenu] = React.useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const navContext = React.useContext<GroupNavContext>(GroupNavContext);
  const huddleToolTip = navContext.huddleGroup ? "Leave Huddle" :
    props.group?.ongoingHuddle ? "Join Huddle" : "Start Huddle";

  function doOpenMenu (event: React.MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
    setOpenMenu(!openMenu);
  }

  function doSettings() {
    setOpenMenu(false);
    if (!props.group) { return; }
    navContext.navTo(`/group/${props.group.groupID}/settings`);
  }

  function doMembers() {
    setOpenMenu(false);
    if (!props.group) { return; }
    navContext.navTo(`/group/${props.group.groupID}/members`);
  }

  function doFiles() {
    setOpenMenu(false);
    if (!props.group) { return; }
    navContext.navTo(`/group/${props.group.groupID}/files`);
  }

  async function doRemoveDevice () {
    try {
      setOpenMenu(false);
      setLoading(true);
      await props.doRemoveThisDevice();
    } finally {
      setLoading(false);
    }
  }

  async function addSelf () {
    if (!props.group) {
      return;
    }
    if (loading) {
      return;
    }
    try {
      setLoading(true);
      await navContext.addThisDeviceToGroup(props.group.groupID);
    } catch (err) {
      console.error("Unable to add self to group:", err);
    } finally {
      setLoading(false);
    }
  }

  function toggleHuddle() {
    if (navContext.huddleGroup) {
      navContext.leaveHuddle()
    } else if (props.group) {
      navContext.doHuddle(props.group.groupID);
    }
  }

  function renderRightButton(): React.ReactElement<any, any> {
    if (loading) {
      return <CircularProgress size={24} />;
    }
    if (props.group?.haveRatchetTree) {
      const canJoinHuddle = props.group.submissiontype !== GroupSubmissionType.broadcastOnly &&
        (props.group.groupMemberType !== GroupMemberType.observer || props.group.ongoingHuddle)
      return <div className="right-group-btns">
        { canJoinHuddle && <IconButton
            onClick={toggleHuddle} aria-label="huddle-button"
            disableRipple disableTouchRipple className="huddle-btn" size="small">
          <Tooltip title={huddleToolTip}>
            <i className="fa-solid fa-video" />
          </Tooltip>
        </IconButton>}
        <IconButton disableRipple aria-label="group-info-menu" aria-haspopup="true"
            size="small" onClick={doOpenMenu}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </div>;
    }
    if (props.group) {
      return <div className="right-group-btns">
        <IconButton aria-label="group-info-menu" aria-haspopup="true"
            size="small" disableRipple disableTouchRipple onClick={addSelf}>
          <Tooltip title="Join Group">
            <i className="fas fa-sign-in-alt" />
          </Tooltip>
        </IconButton>
      </div>;
    }
    return <></>;
  }

  return <div className={"group-header-nav " + props.className + (props.group?.ongoingHuddle ? " with-huddle": "")}>
    <IconButton className="left-nav-outer-toggle show-groups-btn" size="small"
        disableRipple disableTouchRipple
        onClick={navContext.toggleGroupList}>
      <MenuIcon />
    </IconButton>
    <h3>{props.group?.groupname || ""}</h3>
    {renderRightButton()}
    <Popper open={openMenu} anchorEl={anchorEl} disablePortal placement="bottom-end">
      <Paper>
        <ClickAwayListener onClickAway={() => setOpenMenu(false)}>
          <MenuList id="group-meta-menu">
            {props.group?.grouptype !== GroupType.dm &&
              <MenuItem onClick={doSettings} disableRipple disableTouchRipple>
                <ListItemIcon><SettingsIcon /></ListItemIcon>
                <ListItemText>Settings</ListItemText>
              </MenuItem>}
            <MenuItem onClick={doMembers} disableRipple disableTouchRipple>
              <ListItemIcon><PeopleAltIcon /></ListItemIcon>
              <ListItemText>Members</ListItemText>
            </MenuItem>
            <MenuItem key="files" onClick={doFiles} disableRipple disableTouchRipple>
              <ListItemIcon><FolderOpenIcon /></ListItemIcon>
              <ListItemText>Files</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={doRemoveDevice} disableRipple disableTouchRipple>
              <ListItemIcon><LogoutIcon /></ListItemIcon>
              <ListItemText>Remove This Device</ListItemText>
            </MenuItem>
          </MenuList>
        </ClickAwayListener>
      </Paper>
    </Popper>
  </div>;
}